import { Trans } from "@lingui/macro";
import { useCallback } from "react";

import { ASSETCHAIN, ASSETCHAIN_TESTNET, getChainName } from "config/chains";
import { isDevelopment } from "config/env";
import { getIcon } from "config/icons";
import { useGlobalContext } from "context/GlobalContext/GlobalContextProvider";
import { useChainId } from "lib/chains";
import { getAccountUrl, isHomeSite, shouldShowRedirectModal } from "lib/legacy";
import { sendUserAnalyticsConnectWalletClickEvent, userAnalytics } from "lib/userAnalytics";
import { LandingPageLaunchAppEvent } from "lib/userAnalytics/types";
import { useRedirectPopupTimestamp } from "lib/useRedirectPopupTimestamp";
import { useTradePageVersion } from "lib/useTradePageVersion";
import useWallet from "lib/wallets/useWallet";

import { HeaderLink } from "./HeaderLink";
import AddressDropdown from "../AddressDropdown/AddressDropdown";
import ConnectWalletButton from "../Common/ConnectWalletButton";
import LanguagePopupHome from "../NetworkDropdown/LanguagePopupHome";
import NetworkDropdown from "../NetworkDropdown/NetworkDropdown";

import "./Header.scss";

type Props = {
  openSettings: () => void;
  small?: boolean;
  disconnectAccountAndCloseSettings: () => void;
  showRedirectModal: (to: string) => void;
};

const NETWORK_OPTIONS = [
  {
    label: getChainName(ASSETCHAIN),
    value: ASSETCHAIN,
    icon: getIcon(ASSETCHAIN, "network"),
    color: "#3DA58A",
  },
];

if (isDevelopment()) {
  NETWORK_OPTIONS.push({
    label: getChainName(ASSETCHAIN_TESTNET),
    value: ASSETCHAIN_TESTNET,
    icon: getIcon(ASSETCHAIN_TESTNET, "network"),
    color: "#9E6CFF",
  });
}

export function AppHeaderUser({ small, openSettings, disconnectAccountAndCloseSettings, showRedirectModal }: Props) {
  const { chainId } = useChainId();
  const { active, account } = useWallet();
  const { setConnectWalletModalOpen } = useGlobalContext();
  // const { useFallbackRpc, setUseFallbackRpc } = useSettings();
  const showConnectionOptions = !isHomeSite();
  const [tradePageVersion] = useTradePageVersion();
  const [redirectPopupTimestamp] = useRedirectPopupTimestamp();

  const tradeLink = tradePageVersion === 2 ? "/trade" : "/v1";

  const selectorLabel = getChainName(chainId);
  const connectedChainLabel = getChainName(chainId);
  // const showFallbackToggle = chainId === ASSETCHAIN_TESTNET;

  const trackLaunchApp = useCallback(() => {
    userAnalytics.pushEvent<LandingPageLaunchAppEvent>(
      {
        event: "LandingPageAction",
        data: {
          action: "LaunchApp",
          buttonPosition: "StickyHeader",
          shouldSeeConfirmationDialog: shouldShowRedirectModal(redirectPopupTimestamp),
        },
      },
      { instantSend: true }
    );
  }, [redirectPopupTimestamp]);

  // const toggleFallbackRpc = useCallback(() => {
  //   setUseFallbackRpc(!useFallbackRpc);
  // }, [setUseFallbackRpc, useFallbackRpc]);

  // const fallbackToggleTitle = useFallbackRpc
  //   ? t`Fallback RPC enabled`
  //   : t`Fallback RPC disabled`;

  if (!active || !account) {
    return (
      <div className="App-header-user">
        {isHomeSite() ? (
          <div data-qa="trade" className="App-header-trade-link homepage-header text-body-medium">
            <HeaderLink
              className="default-btn"
              onClick={trackLaunchApp}
              to={`${tradeLink}?${userAnalytics.getSessionIdUrlParams()}`}
              showRedirectModal={showRedirectModal}
            >
              <Trans>Launch App</Trans>
            </HeaderLink>
          </div>
        ) : null}

        {showConnectionOptions && setConnectWalletModalOpen ? (
          <>
            <ConnectWalletButton
              onClick={() => {
                sendUserAnalyticsConnectWalletClickEvent("Header");
                setConnectWalletModalOpen(true);
              }}
            >
              <Trans>Connect Wallet</Trans>
            </ConnectWalletButton>
            {/* {!small && <OneClickButton openSettings={openSettings} />} */}
            <NetworkDropdown
              small={small}
              networkOptions={NETWORK_OPTIONS}
              selectorLabel={selectorLabel}
              openSettings={openSettings}
            />
          </>
        ) : (
          <LanguagePopupHome />
        )}
      </div>
    );
  }

  const accountUrl = getAccountUrl(chainId, account);

  return (
    <div className="App-header-user">
      {isHomeSite() ? (
        <div data-qa="trade" className="App-header-trade-link text-body-medium">
          <HeaderLink
            className="default-btn"
            onClick={trackLaunchApp}
            to={`${tradeLink}?${userAnalytics.getSessionIdUrlParams()}`}
            showRedirectModal={showRedirectModal}
          >
            <Trans>Launch App</Trans>
          </HeaderLink>
        </div>
      ) : null}

      {showConnectionOptions ? (
        <a href="#/deposit" className="App-header-deposit-btn" data-qa="header-deposit">
          <span className="App-header-deposit-btn__label text-body-medium">
            <Trans>Deposit</Trans>
          </span>
        </a>
      ) : null}

      <div className="App-header-user-account-summary">
        <div data-qa="user-address" className="App-header-user-address">
          <AddressDropdown
            account={account}
            accountUrl={accountUrl}
            disconnectAccountAndCloseSettings={disconnectAccountAndCloseSettings}
          />
        </div>
        <div className="App-header-user-chain text-body-small">{connectedChainLabel}</div>
      </div>
      {/* {showFallbackToggle && (
        <button
          type="button"
          className={`App-header-fallback-toggle${useFallbackRpc ? " enabled" : ""}`}
          onClick={toggleFallbackRpc}
          title={fallbackToggleTitle}
          aria-pressed={useFallbackRpc}
        >
          <span className="App-header-fallback-toggle__label">
            <Trans>Fallback RPC</Trans>
          </span>
          <span className="App-header-fallback-toggle__switch">
            <span className="App-header-fallback-toggle__handle" />
          </span>
        </button>
      )} */}
      {/* {!small && <OneClickButton openSettings={openSettings} />} */}
      <NetworkDropdown
        small={small}
        networkOptions={NETWORK_OPTIONS}
        selectorLabel={selectorLabel}
        openSettings={openSettings}
      />
      {/* {
        showConnectionOptions ? (
          <>
            <div data-qa="user-address" className="App-header-user-address">
              <AddressDropdown
                account={account}
                accountUrl={accountUrl}
                disconnectAccountAndCloseSettings={disconnectAccountAndCloseSettings}
              />
            </div>
            {!small && <OneClickButton openSettings={openSettings} />}
            <NetworkDropdown
              small={small}
              networkOptions={NETWORK_OPTIONS}
              selectorLabel={selectorLabel}
              openSettings={openSettings}
            />
          </>
        ) : (
          <LanguagePopupHome />
        )
      } */}
    </div>
  );
}
